import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context";
import { getCategories, getProductListPaged, placeOrder, getDefaultImage } from "../api/productApi";
import { getStoredCompanyId } from "../utils/user";
import { sendOrderConfirmation } from "../api/authApi";
import { getOrderStatusList, getOrderSeriesList } from "../api/orderApi";
import { getProfile } from "../api/userApi";
import { useAsyncAction } from "./useAsyncAction";
import { showErrorToast } from "../utils/toast";
import { isProductOutOfStock } from "../utils/productAvailability";

const SELECTED_IDS_KEY = "product_collections_selected_ids";
const SELECTED_PRODUCTS_KEY = "product_collections_selected_products";


const readStorage = (key, fallback) => {
    try {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : fallback;
    } catch {
        return fallback;
    }
};

export const useProductCollectionsData = (mode = "browse") => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const SHOP_ALL = { id: 0, name: t("PRODUCT_COLLECTIONS_SHOP_ALL"), nameEn: "Shop All" };
    const [activeCategory, setActiveCategory] = useState(() => SHOP_ALL);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedIds, setSelectedIds] = useState(() => mode === "checkout" ? readStorage(SELECTED_IDS_KEY, []) : []);
    const [selectedProducts, setSelectedProducts] = useState(() => mode === "checkout" ? readStorage(SELECTED_PRODUCTS_KEY, []) : []);
    const [showSuccess, setShowSuccess] = useState(false);
    const [placedOrderId, setPlacedOrderId] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([SHOP_ALL]);
    const [defaultProductImage, setDefaultProductImage] = useState(null);
    const [processingStatusId, setProcessingStatusId] = useState(null);
    const [formData, setFormData] = useState(() => ({
        firstName: user?.firstName || user?.FirstName || "",
        lastName: user?.lastName || user?.LastName || "",
        email: user?.email || user?.Email || "",
        phone: user?.phone || user?.Phone || "",
        street: user?.street || user?.Street || "",
        number: user?.number || user?.Number || "",
        floor: "",
        city: user?.city || user?.City || "",
        postalCode: user?.postalCode || user?.PostalCode || "",
        area: user?.area || user?.Area || "",
        country: user?.countryId ? String(user.countryId) : "",
    }));

    const categoriesLoadAction = useAsyncAction({ initialLoading: true });
    const productsLoadAction = useAsyncAction();
    const submitAction = useAsyncAction();
    const [hasLoadedCategories, setHasLoadedCategories] = useState(mode === "checkout");
    const [hasLoadedProducts, setHasLoadedProducts] = useState(mode === "checkout");
    const { run: runLoadCategories, isLoading: isLoadingCategories, errorMessage: categoryError } = categoriesLoadAction;
    const { run: runLoadProducts, isLoading: isLoadingProducts, errorMessage: productError } = productsLoadAction;
    const apiError = submitAction.errorMessage || categoryError || productError;
    const isSubmitting = submitAction.isLoading;

    useEffect(() => {
        if (!apiError) return;
        showErrorToast(apiError);
    }, [apiError]);

    useEffect(() => {
        sessionStorage.setItem(SELECTED_IDS_KEY, JSON.stringify(selectedIds));
    }, [selectedIds]);


    useEffect(() => {
        if (mode === "checkout") return;
        let mounted = true;

        const loadCategories = async () => {
            const [result, defaultImageData, statusList] = await Promise.all([
                runLoadCategories(
                    () => getCategories(),
                    { errorFallback: "Unable to load categories.", silentErrorToast: true }
                ),
                getDefaultImage().catch(() => null),
                getOrderStatusList().catch(() => []),
            ]);
            if (!mounted) return;
            const apiCategories = result?.categories || [];
            setCategories([SHOP_ALL, ...apiCategories]);
            setDefaultProductImage(defaultImageData?.productDefaultImage || null);
            const processingStatus = statusList.find((s) => s.title?.toLowerCase() === "processing");
            setProcessingStatusId(processingStatus?.id ?? null);
            setHasLoadedCategories(true);
        };

        loadCategories();
        return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runLoadCategories, mode]);

    const loadProducts = useCallback(async (categoryId, page) => {
        const result = await runLoadProducts(
            () => getProductListPaged({ p_category: categoryId, p_page_number: page }),
            { errorFallback: "Unable to load products.", silentErrorToast: true }
        );
        return result;
    }, [runLoadProducts]);

    useEffect(() => {
        if (mode === "checkout" || !hasLoadedCategories) return;
        let mounted = true;

        const run = async () => {
            const result = await loadProducts(activeCategory?.id ?? 0, currentPage);
            if (!mounted) return;
            const rawProducts = result?.products || [];
            setProducts(
                defaultProductImage
                    ? rawProducts.map((p) => ({ ...p, image: p.image || defaultProductImage }))
                    : rawProducts
            );
            setTotalPages(result?.totalPages ?? 1);
            setHasLoadedProducts(true);
        };

        run();
        return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasLoadedCategories, activeCategory?.id, currentPage, mode]);

    const handleCategorySelect = (category) => {
        setActiveCategory(category);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const orderItems = mode === "checkout"
        ? selectedProducts
        : selectedProducts;

    const handleToggle = (id) => {
        const product = products.find((p) => p.id === id);
        if (product && isProductOutOfStock(product)) {
            showErrorToast("This product is currently out of stock.");
            return;
        }

        if (selectedIds.includes(id)) {
            setSelectedIds((prev) => prev.filter((item) => item !== id));
            setSelectedProducts((prev) => prev.filter((p) => p.id !== id));
            return;
        }

        if (selectedIds.length >= maxProducts) {
            showErrorToast("You can only select up to 5 products.");
            return;
        }

        setSelectedIds((prev) => [...prev, id]);
        if (product) {
            setSelectedProducts((prev) => [...prev, product]);
        }
    };

    const handlePlaceOrder = async (values) => {
        if (!orderItems.length) {
            showErrorToast("Please select at least one product.");
            return;
        }

        const unavailableItems = orderItems.filter((product) => isProductOutOfStock(product));
        if (unavailableItems.length) {
            showErrorToast("Please remove out-of-stock products before placing the order.");
            return;
        }

        const userId = Number(user?.id || user?.Id || user?.userId || user?.UserID || 0);
        if (!userId) {
            showErrorToast("Missing user profile. Please sign in again.");
            return;
        }

        let capturedSeriesItem = null;

        const result = await submitAction.run(
            async () => {
                const seriesData = await getOrderSeriesList().catch(() => null);
                console.log("[Order Series] Response:", seriesData);
                const seriesItem = Array.isArray(seriesData) ? seriesData[0]
                    : seriesData?.Data?.[0] || seriesData?.data?.[0] || null;
                capturedSeriesItem = seriesItem;
                const order_series_id = seriesItem?.orderseriesid ?? null;
                const order_series_number = seriesItem?.orderseries_lastnumber != null
                    ? seriesItem.orderseries_lastnumber + 1 : null;

                const now = new Date().toISOString();
                const items = orderItems.map((product) => {
                    const priceValue = Number(product.priceValue || 0);
                    return {
                        product_id: product.id,
                        product_code: product.code,
                        product_name: product.name,
                        quantity: 1,
                        total: 0,
                        per_kg_price: priceValue,
                        discount_amount: priceValue,
                    };
                });

                const totalAmount = orderItems.reduce((sum, p) => sum + Number(p.priceValue || 0), 0);

                const orderPayload = {
                    CustomerID: userId,
                    Companyid: getStoredCompanyId(),
                    order_date: now,
                    delivery_date: now,
                    contact_email: values.email,
                    RecipientFirstName: values.firstName,
                    RecipientLastName: values.lastName,
                    RecipientPhone: values.phone,
                    RecipientCity: values.city,
                    RecipientFloor: values.floor || "",
                    RecipientNumber: values.number || "",
                    RecipientPostalCode: values.postalCode,
                    RecipientRegion: values.area || "",
                    RecipientStreet: values.street,
                    RecipientCountryId: values.country ? Number(values.country) : null,
                    StatusID: processingStatusId,
                    total_pieces: items.length,
                    PaymentType: "Gift",
                    total_amount: totalAmount,
                    discount_amount: totalAmount,
                    final_amount: 0,
                    order_series_id,
                    order_series_number,
                    sub_total: totalAmount,
                    items,
                };

                console.log("[Place Order] Payload:", orderPayload);
                return placeOrder(orderPayload);
            },
            {
                errorFallback: "Failed to place order. Please try again.",
                silentErrorToast: true,
            }
        );

        if (result) {
            const orderId = result.OrderID || null;
            setPlacedOrderId(orderId);
            setShowSuccess(true);

            const shippingAddress = [values.street, values.number, values.city, values.postalCode, values.area]
                .filter(Boolean).join(", ");

            sendOrderConfirmation({
                Email: values.email,
                FirstName: values.firstName,
                OrderNumber: capturedSeriesItem?.orderseries_lastnumber != null
                    ? String(capturedSeriesItem.orderseries_lastnumber + 1) : "",
                OrderDate: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }),
                ShippingFirstName: values.firstName,
                ShippingLastName: values.lastName,
                ShippingAddress: shippingAddress,
                ShippingPhone: values.phone,
                Items: orderItems.map((product) => ({
                    ProductImage: product.image || "",
                    ProductName: product.name,
                    ProductPrice: `EUR ${Number(product.priceValue || 0).toFixed(2)}`,
                    Quantity: 1,
                })),
            });
        }
    };

    const openCheckout = () => {
        if (!selectedIds.length) {
            showErrorToast("Please select at least one product.");
            return;
        }
        const unavailableItems = selectedProducts.filter((product) => isProductOutOfStock(product));
        if (unavailableItems.length) {
            showErrorToast("Please remove out-of-stock products before continuing.");
            return;
        }
        sessionStorage.setItem(SELECTED_PRODUCTS_KEY, JSON.stringify(selectedProducts));
        navigate("/product-collections/checkout");
    };

    const clearSelection = () => {
        setSelectedIds([]);
        setSelectedProducts([]);
        sessionStorage.removeItem(SELECTED_IDS_KEY);
        sessionStorage.removeItem(SELECTED_PRODUCTS_KEY);
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            street: "",
            number: "",
            city: "",
            postalCode: "",
            area: null,
            country: "",
        });
    };

    const [freshProfile, setFreshProfile] = useState(null);

    useEffect(() => {
        let mounted = true;
        getProfile()
            .then((profile) => {
                if (!mounted) return;
                setFreshProfile(profile);
                if (mode === "checkout" && profile) {
                    setFormData((prev) => ({
                        ...prev,
                        firstName: profile.firstName || prev.firstName,
                        lastName: profile.lastName || prev.lastName,
                        email: profile.email || prev.email,
                        phone: profile.phone || prev.phone,
                        street: profile.street || prev.street,
                        number: profile.number || prev.number,
                        city: profile.city || prev.city,
                        postalCode: profile.postalCode || prev.postalCode,
                        area: profile.area || prev.area,
                        country: profile.countryId ? String(profile.countryId) : prev.country,
                    }));
                }
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, [mode]);

    const profileSource = freshProfile || user;
    const maxOrders = profileSource?.maxOrders ?? null;
    const totalOrders = profileSource?.totalOrders ?? 0;
    const isFirstOrderPreApproved = profileSource?.isFirstOrderPreApproved ?? true;
    const isOrderLimitReached = maxOrders !== null && totalOrders >= maxOrders;
    const maxProducts = profileSource?.maxNumberOfProductPerOrder ?? 5;

    const computeOrderBanner = () => {
        // Not pre-approved — block all orders
        if (!isFirstOrderPreApproved) return { canOrder: false, nextOrderDate: null };

        // First order — pre-approved, allow immediately
        if (totalOrders === 0) return { canOrder: true, nextOrderDate: null };

        // Subsequent orders — check frequency
        const latestOrderDate = profileSource?.latestOrderCreatedOn ?? null;
        const frequency = profileSource?.orderFrequency ?? null;
        if (!latestOrderDate) return { canOrder: true, nextOrderDate: null };
        const [datePart] = latestOrderDate.split(" ");
        const [day, month, year] = datePart.split("-");
        const parsed = new Date(year, month - 1, day);
        if (!frequency) return { canOrder: true, nextOrderDate: null };
        const next = new Date(parsed);
        next.setDate(next.getDate() + frequency);
        const canOrder = next <= new Date();
        return {
            canOrder,
            nextOrderDate: canOrder ? null : next.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
        };
    };

    const orderBanner = computeOrderBanner();
    const isOrderDisabled = isOrderLimitReached || !orderBanner.canOrder;

    return {
        navigate,
        activeCategory,
        handleCategorySelect,
        currentPage,
        totalPages,
        handlePageChange,
        selectedIds,
        showSuccess,
        setShowSuccess,
        placedOrderId,
        categories,
        products,
        formData,
        orderItems,
        isLoadingCategories,
        isLoadingProducts,
        hasLoadedCategories,
        hasLoadedProducts,
        isSubmitting,
        handleToggle,
        handlePlaceOrder,
        openCheckout,
        clearSelection,
        isOrderLimitReached,
        totalOrders,
        maxOrders,
        maxProducts,
        orderBanner,
        isOrderDisabled,
    };
};
